import React, { useState, useEffect, useRef } from 'react';
import './BranchSelector.css';

const BranchSelector = ({ variant = 'header' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(0);
    const dropdownRef = useRef(null);

    const branches = [
        {
            id: 0,
            name: 'Bhiwandi Branch',
            location: 'Thane, IND',
            phones: ['8408800193', '8149575027'],
            hours: '10:00 am - 6:00 pm',
            badge: 'Primary'
        },
        {
            id: 1,
            name: 'Mumbai BKC Branch',
            location: 'Mumbai, IND',
            phones: ['8928231305', '8433646035'],
            hours: '10:00 am - 6:00 pm',
            badge: 'Secondary'
        }
    ];

    const currentBranch = branches[selectedBranch];

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isOpen]);

    const handleSelectBranch = (index) => {
        setSelectedBranch(index);
        setIsOpen(false);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            setIsOpen(!isOpen);
        } else if (event.key === 'ArrowDown' && isOpen) {
            const nextIndex = (selectedBranch + 1) % branches.length;
            setSelectedBranch(nextIndex);
        } else if (event.key === 'ArrowUp' && isOpen) {
            const prevIndex = (selectedBranch - 1 + branches.length) % branches.length;
            setSelectedBranch(prevIndex);
        } else if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };

    if (variant === 'header') {
        return (
            <div className="branch-selector-header" ref={dropdownRef}>
                <button
                    className="branch-toggle-header"
                    onClick={() => setIsOpen(!isOpen)}
                    onKeyDown={handleKeyDown}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-label={`Select branch: ${currentBranch.name}`}
                >
                    <div className="branch-info-header">
                        <div className="branch-location-header">
                            <i className="fa fa-map-marker-alt text-primary me-2"></i>
                            <span className="branch-name-header">{currentBranch.name}</span>
                        </div>
                        <span className="separator mx-2 text-muted">|</span>
                        <div className="branch-phone-header">
                            <i className="fa fa-phone-alt text-primary me-2"></i>
                            {currentBranch.phones.map((phone, idx) => (
                                <React.Fragment key={idx}>
                                    <a href={`tel:+91${phone}`} onClick={(e) => e.stopPropagation()}>
                                        +91 {phone}
                                    </a>
                                    {idx < currentBranch.phones.length - 1 && <span className="mx-1 text-muted">/</span>}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                    <i className={`fa fa-chevron-down chevron-header ${isOpen ? 'open' : ''}`}></i>
                </button>

                {isOpen && (
                    <div className="branch-dropdown-header" role="listbox">
                        {branches.map((branch, index) => (
                            <div
                                key={branch.id}
                                className={`branch-item-header ${selectedBranch === index ? 'active' : ''}`}
                                onClick={() => handleSelectBranch(index)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSelectBranch(index);
                                }}
                                role="option"
                                aria-selected={selectedBranch === index}
                                tabIndex={0}
                            >
                                <div className="branch-item-header-info">
                                    <div className="branch-item-header-title">
                                        <span>{branch.name}</span>
                                    </div>
                                    <p className="branch-item-header-location">
                                        <i className="fa fa-map-marker-alt me-2"></i>
                                        {branch.location}
                                    </p>
                                    <p className="branch-item-header-phones">
                                        {branch.phones.map((phone, idx) => (
                                            <a key={idx} href={`tel:+91${phone}`} onClick={(e) => e.stopPropagation()}>
                                                <i className="fa fa-phone-alt me-2"></i>+91 {phone}
                                                {idx < branch.phones.length - 1 && <br />}
                                            </a>
                                        ))}
                                    </p>
                                </div>
                                {selectedBranch === index && (
                                    <i className="fa fa-check check-icon-header"></i>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // Footer variant - Show only selected branch
    const otherBranches = branches.filter((_, idx) => idx !== selectedBranch);

    return (
        <div className="branch-selector-footer" ref={dropdownRef}>
            <h4 className="text-white mb-3">Our Branches</h4>

            <div className="branch-info-footer">
                <p className="mb-2"><strong>{currentBranch.name}</strong></p>
                <p className="mb-2"><i className="fa fa-map-marker-alt me-2"></i>{currentBranch.location}</p>
                {currentBranch.phones.map((phone, idx) => (
                    <p key={idx} className="mb-2">
                        <i className="fa fa-phone-alt me-2"></i>
                        <a href={`tel:+91${phone}`}>{phone}</a>
                    </p>
                ))}
            </div>

            {otherBranches.length > 0 && (
                <button
                    className="branch-toggle-footer"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                    aria-label="View other branches"
                >
                    <i className={`fa fa-chevron-down ${isOpen ? 'rotate' : ''}`}></i>
                    Other Branches
                </button>
            )}

            {isOpen && otherBranches.length > 0 && (
                <div className="branch-dropdown-footer">
                    {otherBranches.map((branch, index) => (
                        <div
                            key={branch.id}
                            className="branch-item-footer"
                            onClick={() => handleSelectBranch(branches.indexOf(branch))}
                        >
                            <div>
                                <p className="mb-2"><strong>{branch.name}</strong></p>
                                <p className="mb-2"><i className="fa fa-map-marker-alt me-2"></i>{branch.location}</p>
                                {branch.phones.map((phone, idx) => (
                                    <p key={idx} className="mb-1">
                                        <i className="fa fa-phone-alt me-2"></i>
                                        <a href={`tel:+91${phone}`}>{phone}</a>
                                    </p>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BranchSelector;
